import { Suspense, lazy, useCallback, useEffect, useState } from 'react';

import LobbyPage from './pages/LobbyPage';
import EndCallPage from './pages/EndCallPage';
import { ScreenPreloader } from '@/app/components/Preloader';
import { Permissions } from './types';

// The Agora SDK and the in-call providers, ~700KB gzip nothing before the
// call consumes. Lazy keeps them off the join-critical path; the prefetch
// below starts the download the moment the lobby renders, so by the time a
// human has read the checklist and pressed Join it is almost always here.
const CallExperience = lazy(() => import('@/app/CallExperience'));


enum CurrentAppPage {
  LOBBY,
  MEETING,
  END_CALL,
}

export default function AppContainer() {
  const [appPage, setAppPage] = useState<CurrentAppPage>(CurrentAppPage.LOBBY);
  const [permissions, setPermissions] = useState<Permissions>({ audio: false, video: false });

  useEffect(function prefetchCallExperience() {
    // Failure is fine to swallow: the lazy import at Join retries, and an
    // offline blip here should not become Sentry noise.
    import('@/app/CallExperience').catch(() => undefined);
  }, []);

  const onJoin = useCallback((newPermissions: Permissions) => {
    setAppPage(CurrentAppPage.MEETING);
    setPermissions(newPermissions);
  }, []);

  // A call the user ended and one that ended itself look the same from here, but
  // not to the person reading the screen: one of them may well want back in.
  const [wasAbandoned, setWasAbandoned] = useState(false);

  const onEndCall = useCallback(() => {
    setAppPage(CurrentAppPage.END_CALL);
  }, []);

  const onAbandoned = useCallback(() => {
    setWasAbandoned(true);
    setAppPage(CurrentAppPage.END_CALL);
  }, []);

  if (appPage === CurrentAppPage.LOBBY) {
    return <LobbyPage onJoin={onJoin} />;
  } else if (appPage === CurrentAppPage.MEETING) {
    // The fallback mirrors MeetingView's own connecting state, so a join that
    // outruns the prefetch shows one continuous loader, not a new screen.
    return (
      <Suspense
        fallback={
          <div className="dark">
            <ScreenPreloader />
          </div>
        }
      >
        <CallExperience
          onAbandoned={onAbandoned}
          onEndCall={onEndCall}
          permissions={permissions}
        />
      </Suspense>
    );
  } else if (appPage === CurrentAppPage.END_CALL) {
    return <EndCallPage wasAbandoned={wasAbandoned} />;
  }

  const unsupportedPage: never = appPage;
  throw new Error(`We don't support this page yet ${unsupportedPage}`);
}
